import { useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useCollectionBinding, useSlotGroup } from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useCtaSheenStyles } from '../hooks/useCtaSheenStyles';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// Gallery helpers (mirrors theme-vantage `_galleryShared`; inlined because the
// parent helper module is not exposed via the theme's package exports map).
// ============================================================================

interface GalleryImage {
  image: string;
  alt_text?: string;
  title?: string;
  description?: string;
  source_page_urls?: string;
  original_url?: string;
}

/** Classify a gallery photo by its title — used to split community vs unit shots. */
function classifyPhoto(photo: GalleryImage): 'apartment' | 'community' | 'other' {
  const title = (photo.title || '').toLowerCase().trim();
  if (title.includes('apartment')) return 'apartment';
  if (title.includes('community')) return 'community';
  return 'other';
}

// ============================================================================
// Static slot group definitions (for contentSchema — must be inline literals)
// ============================================================================

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Explore Neighborhood CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/neighborhood' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Rotating image cell
// ============================================================================

const ZIGZAG_SLOT_ORDER = [0, 2, 1, 3] as const;
const SLOT_COUNT = 4;
const STEP_MS = 2200;
const AUTO_SCROLL_MS = 4500;

function photoSrc(photo: GalleryImage): string {
  return photo.image ?? photo.original_url ?? '';
}

/** Shortest distance between two indices on a circular gallery. */
function circularDistance(a: number, b: number, count: number): number {
  if (count <= 0) return 0;
  const diff = Math.abs(a - b);
  return Math.min(diff, count - diff);
}

/** Pick four starting indices as far apart as the gallery size allows. */
function computeSpreadIndices(count: number): [number, number, number, number] {
  if (count <= 0) return [0, 0, 0, 0];
  if (count === 1) return [0, 0, 0, 0];
  if (count === 2) return [0, 1, 0, 1];
  if (count === 3) return [0, 1, 2, 0];

  return Array.from({ length: SLOT_COUNT }, (_, slot) =>
    Math.floor((slot * count) / SLOT_COUNT) % count,
  ) as [number, number, number, number];
}

/**
 * Advance one slot to the next index that maximizes separation from the other
 * three cells — avoids duplicates when possible and skips in gallery-sized
 * jumps instead of stepping +1.
 */
function pickNextIndex(current: number, otherIndices: number[], count: number): number {
  if (count <= 1) return 0;

  const occupied = new Set(otherIndices);
  const jump = Math.max(1, Math.round(count / SLOT_COUNT));

  const candidates: number[] = [];
  for (let step = 1; step <= count; step++) {
    const viaJump = (current + step * jump) % count;
    if (!candidates.includes(viaJump)) candidates.push(viaJump);
  }
  for (let step = 1; step <= count; step++) {
    const sequential = (current + step) % count;
    if (!candidates.includes(sequential)) candidates.push(sequential);
  }

  let best = candidates[0] ?? (current + 1) % count;
  let bestScore = -Infinity;

  for (const candidate of candidates) {
    const isDuplicate = occupied.has(candidate);
    const minDist =
      otherIndices.length === 0
        ? count
        : Math.min(...otherIndices.map((other) => circularDistance(candidate, other, count)));

    const score = (isDuplicate ? -10_000 : 0) + minDist * 100 + (candidate === current ? -1 : 0);
    if (score > bestScore) {
      bestScore = score;
      best = candidate;
    }
  }

  return best;
}

interface GridImageCellProps {
  photos: GalleryImage[];
  currentIndex: number;
  className?: string;
}

function GridImageCell({ photos, currentIndex, className }: GridImageCellProps) {
  const count = photos.length;
  const safeCurrent = count > 0 ? currentIndex % count : 0;

  return (
    <div className={`neighborhood-grid-cell ${className ?? ''}`.trim()}>
      {count === 0 ? (
        <div className="absolute inset-0 bg-white" aria-hidden="true" />
      ) : (
        photos.map((photo, i) => {
          const src = photoSrc(photo);
          if (!src) return null;
          const alt = photo.alt_text || photo.title || 'Community photo';
          return (
            <VibesitesImage
              key={`${src}-${i}`}
              url={src}
              alt={alt}
              className={`neighborhood-grid-cell__img${i === safeCurrent ? ' neighborhood-grid-cell__img--active' : ''}`}
              priority={i === safeCurrent}
            />
          );
        })
      )}
    </div>
  );
}

function useZigzagRotation(count: number, paused: boolean) {
  const [cellIndices, setCellIndices] = useState<[number, number, number, number]>(() =>
    computeSpreadIndices(0),
  );
  const stepRef = useRef(0);

  useEffect(() => {
    setCellIndices(computeSpreadIndices(count));
    stepRef.current = 0;
  }, [count]);

  useEffect(() => {
    if (count <= 1 || paused) return;

    const id = setInterval(() => {
      const slot = ZIGZAG_SLOT_ORDER[stepRef.current % ZIGZAG_SLOT_ORDER.length];
      stepRef.current += 1;
      setCellIndices((prev) => {
        const others = [0, 1, 2, 3]
          .filter((i) => i !== slot)
          .map((i) => prev[i]);
        const next = [...prev] as [number, number, number, number];
        next[slot] = pickNextIndex(prev[slot], others, count);
        return next;
      });
    }, STEP_MS);

    return () => clearInterval(id);
  }, [count, paused]);

  return cellIndices;
}

// ============================================================================
// Component
// ============================================================================

export function NeighborhoodSection() {
  const photos = useCollectionBinding<GalleryImage>('neighborhoodsection.photos', {
    collection: 'photo_gallery',
  });

  // Prefer community-classified photos, but fall back to the full gallery when
  // there aren't enough "community"-titled shots to give the four rotating
  // cells any variety. Without this, a gallery with only a couple of photos
  // whose title happens to contain "community" would leave every tile cycling
  // through the same two images.
  const communityPhotos = useMemo(() => {
    const list = photos && photos.length > 0 ? photos : [];
    const withSrc = list.filter((photo) => Boolean(photoSrc(photo)));
    const community = withSrc.filter((photo) => classifyPhoto(photo) === 'community');
    return community.length >= SLOT_COUNT ? community : withSrc;
  }, [photos]);

  const cta = useSlotGroup('neighborhoodsection.cta_link', ctaLinkDef);
  const [paused, setPaused] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReduceMotion(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const gridPaused = paused || reduceMotion;
  const cellIndices = useZigzagRotation(communityPhotos.length, gridPaused);

  // Mobile carousel: track which slide is centered so the dot nav can reflect it.
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeSlide, setActiveSlide] = useState(0);

  const handleTrackScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    const { scrollLeft } = el;
    const slides = Array.from(el.children) as HTMLElement[];
    let nearest = 0;
    let min = Infinity;
    slides.forEach((slide, i) => {
      const distance = Math.abs(slide.offsetLeft - scrollLeft);
      if (distance < min) {
        min = distance;
        nearest = i;
      }
    });
    setActiveSlide(nearest);
  };

  const scrollToSlide = (index: number) => {
    const el = trackRef.current;
    if (!el) return;
    const clamped = Math.max(0, Math.min(SLOT_COUNT - 1, index));
    const slide = el.children[clamped] as HTMLElement | undefined;
    if (!slide) return;
    el.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
  };

  // Auto-advance the mobile carousel. Pauses while the user is actively
  // touching/dragging the track and stays off when reduced motion is requested.
  const interactingRef = useRef(false);

  useEffect(() => {
    if (reduceMotion) return;

    const mobileQuery = window.matchMedia('(max-width: 639px)');
    let timer: ReturnType<typeof setInterval> | undefined;

    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = undefined;
      }
    };

    const start = () => {
      stop();
      if (!mobileQuery.matches) return;
      timer = setInterval(() => {
        const el = trackRef.current;
        if (!el || interactingRef.current) return;
        const slides = Array.from(el.children) as HTMLElement[];
        if (slides.length === 0) return;
        let current = 0;
        let min = Infinity;
        slides.forEach((slide, i) => {
          const distance = Math.abs(slide.offsetLeft - el.scrollLeft);
          if (distance < min) {
            min = distance;
            current = i;
          }
        });
        const next = slides[(current + 1) % slides.length];
        el.scrollTo({ left: next.offsetLeft, behavior: 'smooth' });
      }, AUTO_SCROLL_MS);
    };

    start();
    mobileQuery.addEventListener('change', start);
    return () => {
      stop();
      mobileQuery.removeEventListener('change', start);
    };
  }, [reduceMotion]);

  useRevealStyles();
  useCtaSheenStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up w-full overflow-hidden bg-secondary py-10 md:py-12 lg:py-14"
    >
      <div className="mx-auto flex flex-col items-start gap-8 px-4 sm:px-6 lg:grid lg:max-w-none lg:grid-cols-2 lg:items-stretch lg:gap-6 lg:px-0 xl:gap-10">
        {/* Left: copy on white panel — bleeds to viewport left, ends at center on lg+ */}
        <div className="relative z-10 -mx-4 flex w-[calc(100%+2rem)] flex-col bg-white px-8 py-8 sm:-mx-6 sm:w-[calc(100%+3rem)] sm:px-10 sm:py-10 md:px-12 md:py-12 lg:mx-0 lg:w-full lg:px-12 lg:pr-14 xl:px-16 xl:pr-20">
          <RichTextContent
              path="neighborhoodsection.eyebrow"
              label="Eyebrow"
              defaultValue="<p>Ideally Located</p>"
              as="p"
              className="font-body mb-4 text-xs uppercase tracking-[0.25em] text-foreground sm:text-sm"
            />

            <RichTextContent
              path="neighborhoodsection.heading"
              label="Heading"
              defaultValue='<p><span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>'
              as="h2"
              className="font-heading mb-6 text-3xl font-bold uppercase leading-[1.1] text-foreground sm:text-4xl md:text-[2.75rem]"
            />

            <div className="mb-8 h-1 w-16 rounded-full bg-primary" aria-hidden="true" />

            <RichTextContent
              path="neighborhoodsection.body"
              label="Body Text"
              defaultField="property_home_neighborhood_blurb"
              defaultValue="<p>Experience the best of downtown living with easy access to dining, entertainment, and cultural destinations. Our prime location puts you at the center of it all while offering a peaceful retreat to call home.</p>"
              as="div"
              className="font-body mb-10 max-w-xl text-base leading-relaxed text-muted-foreground [&_a]:text-primary [&_a]:underline"
            />

            <EditableSlotGroup
              groupPath="neighborhoodsection.cta_link"
              definition={ctaLinkDef}
              className="inline-block"
            >
              <a
                href={cta.url || '/neighborhood'}
                target={cta._target || '_self'}
                className="neighborhood-cta vantage-cta-sheen inline-flex items-center justify-center px-8 py-3.5 font-heading text-sm uppercase tracking-wider"
              >
                <span className="relative z-10">
                  <RichTextContent
                    path="neighborhoodsection.cta_label"
                    label="CTA Label"
                    defaultValue="<p>Explore the Neighborhood</p>"
                    as="span"
                  />
                </span>
              </a>
          </EditableSlotGroup>
        </div>

        {/* Right: auto-rotating community photos — swipeable carousel on mobile,
            2×2 grid from the sm breakpoint up. */}
        <div
          className="neighborhood-photos relative z-0 w-full max-w-xl sm:max-w-none lg:max-w-none lg:pl-4 lg:pr-8 xl:pr-12"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div
            ref={trackRef}
            className="neighborhood-photo-track"
            onScroll={handleTrackScroll}
            onPointerDown={() => {
              interactingRef.current = true;
            }}
            onPointerUp={() => {
              interactingRef.current = false;
            }}
            onPointerCancel={() => {
              interactingRef.current = false;
            }}
          >
            {cellIndices.map((index, i) => (
              <div className="neighborhood-photo-slide" key={i}>
                <GridImageCell photos={communityPhotos} currentIndex={index} />
              </div>
            ))}
          </div>

          <div className="neighborhood-photo-dots" role="tablist" aria-label="Community photos">
            {cellIndices.map((_, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={i === activeSlide}
                aria-label={`Go to photo ${i + 1}`}
                className={`neighborhood-photo-dot${i === activeSlide ? ' neighborhood-photo-dot--active' : ''}`}
                onClick={() => scrollToSlide(i)}
              />
            ))}
          </div>
        </div>
      </div>

      <style>{`
        .neighborhood-cta [data-vibesite-richtext],
        .neighborhood-cta [data-vibesite-richtext] p,
        .neighborhood-cta [data-vibesite-richtext] span {
          margin: 0;
          font: inherit;
          color: inherit;
          text-transform: inherit;
          letter-spacing: inherit;
        }

        /* Mobile: horizontal swipe + auto-scrolling carousel, one photo per slide. */
        .neighborhood-photo-track {
          display: flex;
          gap: 0.625rem;
          overflow-x: auto;
          scroll-snap-type: x mandatory;
          scroll-behavior: smooth;
          -webkit-overflow-scrolling: touch;
          /* Keep horizontal swipes inside the carousel (no browser back/forward
             gesture or page bounce) while leaving vertical page scroll free. */
          touch-action: pan-x pan-y;
          overscroll-behavior-x: contain;
          scrollbar-width: none;
        }
        .neighborhood-photo-track::-webkit-scrollbar {
          display: none;
        }
        .neighborhood-photo-slide {
          flex: 0 0 100%;
          min-width: 0;
          scroll-snap-align: center;
        }
        .neighborhood-photo-dots {
          display: flex;
          justify-content: center;
          gap: 0.5rem;
          margin-top: 0.875rem;
        }
        .neighborhood-photo-dot {
          width: 0.5rem;
          height: 0.5rem;
          padding: 0;
          border: none;
          border-radius: 999px;
          background: color-mix(in srgb, var(--vibe-color-primary) 28%, transparent);
          cursor: pointer;
          transition: background 0.2s ease, transform 0.2s ease;
        }
        .neighborhood-photo-dot--active {
          background: var(--vibe-color-primary);
          transform: scale(1.3);
        }

        /* sm and up: restore the auto-rotating 2×2 grid. */
        @media (min-width: 640px) {
          .neighborhood-photo-track {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 0.75rem;
            overflow: visible;
            scroll-snap-type: none;
          }
          .neighborhood-photo-slide {
            flex: none;
            scroll-snap-align: none;
          }
          .neighborhood-photo-dots {
            display: none;
          }
        }

        .neighborhood-grid-cell {
          position: relative;
          aspect-ratio: 1 / 1;
          width: 100%;
          overflow: hidden;
          background: #fff;
        }
        .neighborhood-grid-cell__img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          opacity: 0;
          transition: opacity 0.7s ease;
        }
        .neighborhood-grid-cell__img--active {
          opacity: 1;
        }
        @media (prefers-reduced-motion: reduce) {
          .neighborhood-grid-cell__img {
            transition: none;
          }
          .neighborhood-photo-track {
            scroll-behavior: auto;
          }
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodSection',
  displayName: 'Neighborhood Section',
  tokens: {
    'neighborhoodsection.eyebrow': {
      label: 'Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Ideally Located</p>',
    },
    'neighborhoodsection.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>',
    },
    'neighborhoodsection.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultField: 'property_home_neighborhood_blurb',
      defaultValue:
        '<p>Experience the best of downtown living with easy access to dining, entertainment, and cultural destinations. Our prime location puts you at the center of it all while offering a peaceful retreat to call home.</p>',
    },
    'neighborhoodsection.cta_label': {
      label: 'CTA Label',
      type: 'richtext',
      defaultValue: '<p>Explore the Neighborhood</p>',
    },
  },
  slotGroups: {
    'neighborhoodsection.cta_link': ctaLinkDef,
  },
  collectionBindings: {
    'neighborhoodsection.photos': {
      label: 'Community Photos',
      collection: 'photo_gallery',
      description:
        'Community gallery photos that auto-rotate in the 2×2 grid (a swipeable carousel on mobile). Each cell starts on a different photo and cycles through the set.',
    },
  },
};

(NeighborhoodSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default NeighborhoodSection;
